import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, LogOut, Power, PowerOff, ShieldAlert, ShieldOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

function Resultado({ ok, texto }) {
  const Icono = ok ? CheckCircle2 : ShieldAlert;
  return (
    <div className={`mt-6 flex gap-3 rounded-2xl border p-4 text-sm ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-700"}`} data-testid={ok ? "cerrar-ceo-ok" : "cerrar-ceo-error"}>
      <Icono className="mt-0.5 h-5 w-5 shrink-0" /> <p>{texto}</p>
    </div>
  );
}

const MODOS = {
  cerrar: {
    ruta: "/ceo/cerrar-sesiones", titulo: "Cerrar todas las sesiones CEO", boton: "Cerrar todas las sesiones", icon: LogOut,
    texto: "Se cerrarán al momento todas las sesiones abiertas del panel ADMINISTRACIÓN CEO, en cualquier dispositivo. Para volver a entrar habrá que escribir la clave.",
    ok: "Hecho: se han cerrado todas las sesiones CEO abiertas. Para volver a entrar hará falta la clave. Si no fuiste tú quien entró, cambia la clave CEO y la contraseña de la cuenta.",
    error: "No se han podido cerrar las sesiones. Inténtalo de nuevo.",
  },
  estricto: {
    ruta: "/ceo/desactivar-estricto", titulo: "Desactivar el modo estricto", boton: "Desactivar el modo estricto", icon: ShieldOff,
    texto: "El panel ADMINISTRACIÓN CEO volverá a abrirse desde cualquier país que no esté prohibido, siempre con la clave. Podrás activarlo otra vez en Seguridad.",
    ok: "Hecho: el modo estricto está desactivado. Ya puedes entrar al panel CEO con la clave desde este país.",
    error: "No se ha podido desactivar el modo estricto. Inténtalo de nuevo.",
  },
  cerebro: {
    ruta: "/ceo/cerebro/desactivar", titulo: "Desactivar la API de Cerebro", boton: "Desactivar la API de Cerebro", icon: PowerOff,
    texto: "Cerebro dejará de poder consultar los datos de NuvliPro al momento. El resto de la app sigue funcionando igual. Podrás volver a activarla en el panel CEO → Seguridad.",
    ok: "Hecho: la API de Cerebro está desactivada y rechaza cualquier consulta. Te hemos enviado un email de confirmación con un botón para volver a activarla. También puedes hacerlo en el panel CEO → Seguridad.",
    error: "No se ha podido desactivar la API de Cerebro. Inténtalo de nuevo.",
  },
  cerebro_on: {
    ruta: "/ceo/cerebro/reactivar", titulo: "Volver a activar la API de Cerebro", boton: "Activar la API de Cerebro", icon: Power,
    texto: "Cerebro podrá volver a consultar los datos de NuvliPro con su clave CEREBRO_API_KEY. Si cambiaste la clave, pega la nueva también en Cerebro.",
    ok: "Hecho: la API de Cerebro vuelve a estar activada. Puedes desactivarla cuando quieras en el panel CEO → Seguridad.",
    error: "No se ha podido activar la API de Cerebro. Inténtalo de nuevo.",
  },
};

export default function CerrarSesionesCeo({ modo = "cerrar" }) {
  const m = MODOS[modo];
  const Icono = m.icon;
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [estado, setEstado] = useState(null);
  const [busy, setBusy] = useState(false);
  const cerrar = async () => {
    setBusy(true);
    try {
      await api.post(m.ruta, { token });
      setEstado({ ok: true, texto: m.ok });
    } catch (e) {
      setEstado({ ok: false, texto: formatApiErrorDetail(e.response?.data?.detail) || m.error });
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-10 sm:py-16" data-testid="cerrar-sesiones-ceo">
      <main className="mx-auto max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex items-center gap-2.5">
          <img src="/nuvlipro-logo.png" alt="NuvliPro" className="h-9 w-9 rounded-xl border border-sky-100 bg-white p-1 object-contain" />
          <span className="font-heading text-lg font-extrabold text-[#1B2B4B]">NuvliPro</span>
        </div>
        <h1 className="mt-6 font-heading text-2xl font-extrabold text-[#1B2B4B] sm:text-3xl">{m.titulo}</h1>
        <p className="mt-2 text-sm text-slate-600">{m.texto}</p>
        {!token && <Resultado ok={false} texto="Falta el enlace del email. Ábrelo de nuevo desde el aviso que has recibido." />}
        {token && !estado?.ok && (
          <Button onClick={cerrar} disabled={busy} className="mt-6 w-full rounded-full bg-[#1B2B4B] py-6 text-base font-semibold text-white hover:bg-[#13213b]" data-testid="cerrar-ceo-confirmar">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icono className="mr-2 h-4 w-4" />} {m.boton}
          </Button>
        )}
        {estado && <Resultado ok={estado.ok} texto={estado.texto} />}
        <Link to="/" className="mt-6 inline-block text-sm font-medium text-[#0284C7] hover:underline" data-testid="cerrar-ceo-inicio">Ir a NuvliPro</Link>
      </main>
    </div>
  );
}
